import { cn } from '@/lib/utils';
import type { RecurringSchedule } from '@/types/donation';
import { Calendar, RefreshCw } from 'lucide-react';

interface RecurringToggleProps {
  value: RecurringSchedule;
  onChange: (schedule: RecurringSchedule) => void;
  amount: number; // In smallest unit for preview
  currencySymbol: string;
}

function RecurringToggle({ value, onChange, amount, currencySymbol }: RecurringToggleProps) {
  const options: Array<{
    value: RecurringSchedule;
    label: string;
    description: string;
    icon: typeof Calendar | typeof RefreshCw;
  }> = [
    {
      value: 'one_time',
      label: 'One-time',
      description: 'Make a single donation',
      icon: Calendar,
    },
    {
      value: 'monthly',
      label: 'Monthly',
      description: `${currencySymbol}${(amount / 100).toLocaleString()} every month`,
      icon: RefreshCw,
    },
    {
      value: 'yearly',
      label: 'Yearly',
      description: `${currencySymbol}${(amount / 100).toLocaleString()} every year`,
      icon: RefreshCw,
    },
  ];

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-300 uppercase tracking-wider">
        Donation Schedule
        <span className="text-gold ml-1">*</span>
      </label>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={cn(
                'p-4 rounded-lg border-2 text-left transition-all duration-200',
                'hover:border-gold hover:bg-gold/10',
                'focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-dark',
                isSelected
                  ? 'border-gold bg-gold/20'
                  : 'border-dark-border bg-dark-card'
              )}
              aria-pressed={isSelected}
            >
              <div className="flex items-start gap-3">
                <Icon
                  className={cn(
                    'w-5 h-5 mt-0.5 flex-shrink-0',
                    isSelected ? 'text-gold' : 'text-gray-400'
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div
                    className={cn(
                      'font-semibold mb-1',
                      isSelected ? 'text-gold' : 'text-gray-300'
                    )}
                  >
                    {option.label}
                  </div>
                  <div className="text-sm text-gray-400">{option.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Recurring donation disclaimer */}
      {value !== 'one_time' && (
        <div className="mt-4 p-4 bg-dark-lighter border border-gold/30 rounded-lg">
          <p className="text-sm text-gray-300">
            <strong className="text-gold">Note:</strong> You will be charged{' '}
            <strong className="text-gold">
              {currencySymbol}
              {(amount / 100).toLocaleString()}
            </strong>{' '}
            {value === 'monthly' ? 'every month' : 'every year'} starting today. You can cancel
            anytime from your donor portal.
          </p>
        </div>
      )}
    </div>
  );
}

export default RecurringToggle;
